<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formValidierung from "@/businesslogic/formValidierung";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import { NavvItem } from "@/model/NavItem";
import Logger from "js-logger";
import Adresse from "@/components/person/Adresse.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BasisdatenPHV2025",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Adresse,
    Zelle,
    TitelZelle,
    Zeile,
    Ueberschrift,
    ValidierungsFehler,
    Steps,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  setup(_props, { emit }) {
    const piniaStore = useFormStore();
    const fehler = ref({} as any);

    function eingabenSindValide() {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    }

    function nextPage() {
      Logger.debug("Basisdaten", "nextPage", piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum);
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          piniaStore.state.neuberechnen = true;
        }
      });
    }

    const plzZelleTitel = computed(() => {
      if (piniaStore.state.isMakler) {
        return "Postleitzahl";
      }
      return "Postleitzahl Ihres Wohnortes";
    });

    const adresseInfotext = computed(() => {
      if (piniaStore.state.isMakler) {
        return "Die Preise sind abhängig vom Wohnort";
      }
      return "Die Preise sind abhängig von Ihrem Wohnort.";
    });

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 0 });
        }
      },
    );

    return {
      piniaStore,
      nextPage,
      fehler,
      plzZelleTitel,
      adresseInfotext,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="0"></Steps>

  <section>
    <ueberschrift v-if="!piniaStore.state.isMakler" titel="Ihre Angaben" />

    <zeile>
      <titel-zelle><strong>Wen</strong> möchten Sie versichern?</titel-zelle>
      <zelle class="iconRadioPHV">
        <input
          id="tarifVariante1"
          name="tarifVariante"
          v-model="piniaStore.state.tarifinformationen.tarifVariante"
          type="radio"
          value="991"
          required
          data-fehlermeldung="Bitte wählen Sie den zu versichernden Personenkreis aus."
          data-cy="tarifVariante1"
          tabindex="0"
          autofocus
          class="no-outline"
          aria-required="true"
          aria-label="Single"
        />
        <input
          id="tarifVariante2"
          name="tarifVariante"
          v-model="piniaStore.state.tarifinformationen.tarifVariante"
          type="radio"
          value="992"
          data-cy="tarifVariante2"
          class="no-outline"
          aria-label="Single mit Kind oder Kindern"
        />
        <input
          id="tarifVariante3"
          name="tarifVariante"
          v-model="piniaStore.state.tarifinformationen.tarifVariante"
          type="radio"
          value="993"
          data-cy="tarifVariante3"
          class="no-outline"
          aria-label="Paar ohne Kinder"
        />
        <input
          id="tarifVariante4"
          name="tarifVariante"
          v-model="piniaStore.state.tarifinformationen.tarifVariante"
          type="radio"
          value="994"
          data-cy="tarifVariante4"
          class="no-outline"
          aria-label="Paar mit Kind oder Kindern"
        />
        <validierungs-fehler :fehler-text="fehler.tarifVariante" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <zeile>
      <titel-zelle>{{ !piniaStore.state.isMakler ? "Ihr " : "" }} Geburtsdatum</titel-zelle>
      <zelle :breite="6">
        <input
          style="width: 50%"
          type="date"
          id="geburtsdatum"
          name="geburtsdatum"
          v-model="piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum"
          class="c-form-control__input"
          placeholder="TT.MM.JJJJ"
          title="Geburtsdatum im Format TT.MM.JJJJ"
          required
          aria-required="true"
          aria-label="Geben Sie Ihr Geburtsdatum in der Formatierung TT.MM.JJJJ ein."
          data-checkalter18bis100="true"
          data-cy="versicherungsnehmerGeburtstag"
        />
        <validierungs-fehler :fehler-text="fehler.geburtsdatum" />
        <p v-if="piniaStore.state.isMakler" class="sechzig-plus-info">
          Versicherungsnehmer ab 60 Jahren werden in diesem Tarifrechner automatisch dem günstigen 60+ Tarif zugeordnet.
        </p>
      </zelle>
    </zeile>

    <adresse
      :fehler="fehler"
      :placeholder="true"
      v-model:plz="piniaStore.state.personendaten.versicherungsnehmer.adresse.plz"
      v-model:ort="piniaStore.state.personendaten.versicherungsnehmer.adresse.ort"
      v-model:strasse="piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse"
      v-model:hausnummer="piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer"
      :feldNamen="{
        plz: 'versicherungsnehmerPlz',
        ort: 'versicherungsnehmerOrt',
        strasse: 'versicherungsnehmerStrasse',
        hausnummer: 'versicherungsnehmerHausnummer',
      }"
      :sichtbare-felder="['plz']"
      :clear-ort-on-plz-focus="true"
      ref="risikoAdresseRef"
      :zeige-info="true"
      :info-text="adresseInfotext"
    >
      <template v-slot:plzOrtLabel>
        {{ plzZelleTitel }}
      </template>
    </adresse>

    <zeile>
      <zelle :breite="3" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          data-cy="submitZumVergleich"
          aria-label="Weiter zum Vergleich"
        >
          Weiter zum Vergleich
        </button>
      </zelle>
    </zeile>
  </section>
</template>

<style lang="scss" scoped>
.sechzig-plus-info {
  color: #6c757d;
}
</style>
